import * as yup from 'yup';
import { Row, RenderConfig, Column, EMPTY_ARRAY, TextInput, NumberInput, SelectInput, BOOLEAN_OPTIONS } from 'commons-ui';
import _isNil from 'lodash/isNil';

export const validationSchema = yup.object().shape({
	userLevelsSupported: yup.string().matches(/^\d(,\d)*$/, 'User Levels Supported should be in 1,2,5 like format'),
	tournamentComingSoonTime: yup
		.string()
		.nullable(true)
		.matches(/^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/, {
			message: 'Coming Soon Time should be in HH:MM:SS format',
			excludeEmptyString: true
		})
		.optional(),
	tournamentDisplayTime: yup.string().matches(/^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/, {
		message: 'Display Time should be in HH:MM:SS format',
		excludeEmptyString: true
	}),
	tournamentRegistrationStartTime: yup
		.string()
		.matches(/^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/, 'Regsitration Start Time should be in HH:MM:SS format'),
	tournamentStartTime: yup
		.string()
		.matches(/^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/, 'Tournamnet Start Time should be in HH:MM:SS format'),
	segmentsList: yup
		.string()
		.nullable(true)
		.matches(/^[\w\s]+(,[\w\s]+)*$/, {
			message: 'Segments List should be comma-separated values (numbers or strings)',
			excludeEmptyString: true
		})
		.optional(),
	coinEntryFee: yup.number().integer().min(0).optional(),
	coinDistribution: yup
		.array()
		.of(
			yup.object().shape({
				rank: yup.number().integer().min(1).required('Rank is required'),
				zcoins: yup.number().integer().min(0).required('ZCoins is required')
			})
		)
		.optional()
});

export const FORM_FIELD_KEYS = {
	ENTRY_FEE: 'entryFee',
	ROUNDS: 'numberOfRounds',
	PLAYER_PER_TABLE: 'playersPerTable',
	WINNER_PER_TABLE: 'winnersPerTable',
	MAX_WINNERS: 'maxWinners',
	USER_LEVEL_SUPPORTED: 'userLevelsSupported',
	TOURNAMENT_DISPLAY_TIME: 'tournamentDisplayTime',
	REGISTRATION_START_TIME: 'tournamentRegistrationStartTime',
	TOURNAMENT_START_TIME: 'tournamentStartTime',
	TOURNAMENT_COMMING_SOON_TIME: 'tournamentComingSoonTime',
	NAME: 'name',
	IS_SPECIAL: 'isSpecial',
	IS_ACTIVE: 'isActive',
	IS_QUICK_TOURNAMENT: 'isQuickTournament',
	TOTAL_DISTRIBUTIONS: 'totalDistributions',
	PRIZE_GROUPS: 'prizeGroups',
	RANK: 'rank',
	PRIZE: 'prize',
	INSTRUCTION: 'instruction',
	SEGMENTS_LIST: 'segmentsList',
	TIME_LIMIT_IN_SEC: 'timeLimitInSecs',
	STT: 'stt',
	RAKEBACK_POINTS: 'rakebackPoints',
	ENABLE_ML_MATCHMAKING: 'enableMLMatchMaking',
	COIN_ENTRY_FEE: 'coinEntryFee',
	COIN_DISTRIBUTION: 'coinDistribution',
	COIN_RANK: 'coinRank',
	ZCOINS: 'zcoins'
};

const SEGMENT_INSTRUCTION_OPTIONS = [
	{ id: null, label: 'Nil' },
	{ id: 'INCLUDE', label: 'INCLUDE' },
	{ id: 'EXCLUDE', label: 'EXCLUDE' }
];

// Helper function to create distribution arrays based on maxWinners
const createDistributionArray = (maxWinners: number, valueKey: string, existingData: any[] = []) => {
	// Create a map of existing ranks and their values
	const existingRanks = new Map();
	if (existingData && existingData.length > 0) {
		existingData.forEach((item: any) => {
			if (item.rank) {
				existingRanks.set(item.rank, item[valueKey]);
			}
		});
	}

	// Create distribution array with existing values preserved and missing ranks filled with 0
	const distribution = [];
	for (let rank = 1; rank <= maxWinners; rank += 1) {
		distribution.push({
			rank,
			[valueKey]: existingRanks.has(rank) ? existingRanks.get(rank) : 0
		});
	}
	return distribution;
};

// Dependency resolver for prize groups
const prizeGroupsDependencyResolver = (props: any, dependencyValues: any) => {
	const maxWinners = dependencyValues[0];
	const { setValue, watch } = props;

	if (_isNil(maxWinners) || maxWinners <= 0) {
		return null;
	}

	const numWinners = Number(maxWinners);
	if (numWinners > 0) {
		// Get current prize groups to preserve existing values
		const currentPrizeGroups = watch('prizeGroups') || [];
		const newPrizeGroups = createDistributionArray(numWinners, 'prize', currentPrizeGroups);
		setValue('prizeGroups', newPrizeGroups);
	}

	return null;
};

// Dependency resolver for coin distribution
const coinDistributionDependencyResolver = (props: any, dependencyValues: any) => {
	const maxWinners = dependencyValues[0];
	const { setValue, watch } = props;

	if (_isNil(maxWinners) || maxWinners <= 0) {
		return null;
	}

	const numWinners = Number(maxWinners);
	if (numWinners > 0) {
		// Get current coin distribution to preserve existing values
		const currentCoinDistribution = watch('coinDistribution') || [];
		const newCoinDistribution = createDistributionArray(numWinners, 'zcoins', currentCoinDistribution);
		setValue('coinDistribution', newCoinDistribution);
	}

	return null;
};

export const FORM_LAYOUT_CONFIG = [
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.ENTRY_FEE)]), new Column([new Row(FORM_FIELD_KEYS.ROUNDS)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.PLAYER_PER_TABLE)]),
		new Column([new Row(FORM_FIELD_KEYS.WINNER_PER_TABLE)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.MAX_WINNERS)]),
		new Column([new Row(FORM_FIELD_KEYS.USER_LEVEL_SUPPORTED)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.TOURNAMENT_COMMING_SOON_TIME)]),
		new Column([new Row(FORM_FIELD_KEYS.TOURNAMENT_DISPLAY_TIME)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.REGISTRATION_START_TIME)]),
		new Column([new Row(FORM_FIELD_KEYS.TOURNAMENT_START_TIME)])
	]),
	new Row(FORM_FIELD_KEYS.PRIZE_GROUPS, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.RANK)]),
		new Column([new Row(FORM_FIELD_KEYS.PRIZE)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.COIN_ENTRY_FEE)])]),
	new Row(FORM_FIELD_KEYS.COIN_DISTRIBUTION, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.COIN_RANK)]),
		new Column([new Row(FORM_FIELD_KEYS.ZCOINS)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.IS_SPECIAL)]),
		new Column([new Row(FORM_FIELD_KEYS.IS_ACTIVE)]),
		new Column([new Row(FORM_FIELD_KEYS.IS_QUICK_TOURNAMENT)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.RAKEBACK_POINTS)]),
		new Column([new Row(FORM_FIELD_KEYS.TIME_LIMIT_IN_SEC)]),
		new Column([new Row(FORM_FIELD_KEYS.STT)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.SEGMENTS_LIST)]),
		new Column([new Row(FORM_FIELD_KEYS.INSTRUCTION)]),
		new Column([new Row(FORM_FIELD_KEYS.ENABLE_ML_MATCHMAKING)])
	])
];

export const FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS.ENTRY_FEE]: new RenderConfig()
		.setName('entryFee')
		.setLabel('Entry Fee')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.ROUNDS]: new RenderConfig()
		.setName('numberOfRounds')
		.setLabel('Number of Rounds')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.PLAYER_PER_TABLE]: new RenderConfig()
		.setName('playersPerTable')
		.setLabel('Player Per Table')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.WINNER_PER_TABLE]: new RenderConfig()
		.setName('winnersPerTable')
		.setLabel('Winners Per Table')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.MAX_WINNERS]: new RenderConfig()
		.setName('maxWinners')
		.setLabel('Total Number of Winners')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.USER_LEVEL_SUPPORTED]: new RenderConfig()
		.setName('userLevelsSupported')
		.setLabel('User Levels Supported')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.TOURNAMENT_COMMING_SOON_TIME]: new RenderConfig()
		.setName('tournamentComingSoonTime')
		.setLabel('Coming Soon Time')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true }
		}),
	[FORM_FIELD_KEYS.TOURNAMENT_DISPLAY_TIME]: new RenderConfig()
		.setName('tournamentDisplayTime')
		.setLabel('Display Time')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.REGISTRATION_START_TIME]: new RenderConfig()
		.setName('tournamentRegistrationStartTime')
		.setLabel('Registration Start Time')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.TOURNAMENT_START_TIME]: new RenderConfig()
		.setName('tournamentStartTime')
		.setLabel('Tournament Start Time')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.PRIZE_GROUPS]: new RenderConfig()
		.setName('prizeGroups')
		.setLabel('Prize Groups')
		.setRenderProps({
			isArrayField: true,
			isEditable: { create: true, edit: true },
			required: true,
			dependencies: ['maxWinners'],
			dependencyResolver: prizeGroupsDependencyResolver
		}),
	[FORM_FIELD_KEYS.RANK]: new RenderConfig()
		.setName('rank')
		.setLabel('Rank')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.PRIZE]: new RenderConfig()
		.setName('prize')
		.setLabel('Prize')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.SEGMENTS_LIST]: new RenderConfig()
		.setName('segmentsList')
		.setLabel('Segments List')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true }
		}),
	[FORM_FIELD_KEYS.INSTRUCTION]: new RenderConfig()
		.setName('instruction')
		.setLabel('Segment Instruction')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: SEGMENT_INSTRUCTION_OPTIONS
		}),
	[FORM_FIELD_KEYS.IS_SPECIAL]: new RenderConfig()
		.setName('isSpecial')
		.setLabel('isSpecial')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS,
			required: true
		}),
	[FORM_FIELD_KEYS.IS_ACTIVE]: new RenderConfig()
		.setName('isActive')
		.setLabel('isActive')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS,
			required: true
		}),
	[FORM_FIELD_KEYS.IS_QUICK_TOURNAMENT]: new RenderConfig()
		.setName('isQuickTournament')
		.setLabel('isQuickTournament')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS,
			required: true
		}),
	[FORM_FIELD_KEYS.TIME_LIMIT_IN_SEC]: new RenderConfig()
		.setName('timeLimitInSecs')
		.setLabel('Time Limit In Seconds')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true }
		}),
	[FORM_FIELD_KEYS.STT]: new RenderConfig()
		.setName('stt')
		.setLabel('STT (Turn Time)')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true }
		}),
	[FORM_FIELD_KEYS.RAKEBACK_POINTS]: new RenderConfig()
		.setName('rakebackPoints')
		.setLabel('Rakeback Points')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true }
		}),
	[FORM_FIELD_KEYS.ENABLE_ML_MATCHMAKING]: new RenderConfig()
		.setName('enableMLMatchMaking')
		.setLabel('enableMLMatchMaking')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			options: BOOLEAN_OPTIONS,
			required: true
		}),
	[FORM_FIELD_KEYS.COIN_ENTRY_FEE]: new RenderConfig()
		.setName('coinEntryFee')
		.setLabel('Coin Entry Fee')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false
		}),
	[FORM_FIELD_KEYS.COIN_DISTRIBUTION]: new RenderConfig()
		.setName('coinDistribution')
		.setLabel('Coin Distribution')
		.setRenderProps({
			isArrayField: true,
			isEditable: { create: true, edit: true },
			required: false,
			dependencies: ['maxWinners'],
			dependencyResolver: coinDistributionDependencyResolver
		}),
	[FORM_FIELD_KEYS.COIN_RANK]: new RenderConfig()
		.setName('rank')
		.setLabel('Rank')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.ZCOINS]: new RenderConfig()
		.setName('zcoins')
		.setLabel('zcoins')
		.setRenderer(NumberInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		})
};

export const getFormDefaults = () => {
	return {
		winnersPerTable: 1,
		isSpecial: false,
		isActive: true,
		isQuickTournament: false,
		enableMLMatchMaking: false,
		prizeGroups: [],
		coinDistribution: [],
		segmentsList: '',
		instruction: '',
		userLevelsSupported: '1,2'
	};
};
